import type { Filter, FilterDataMultiSelect, FilterDataOption } from '@/app/common/filters/core/filtersTypes.ts';
import type { HandleFilterChanged } from '@/app/common/filters/core/filterComponentType.ts';

export const multiSelectDataOf = (filter: Filter) => filter.data as FilterDataMultiSelect;

export const isOptionSelected = (option: FilterDataOption): boolean => !!option.selected && !option.isDefault;

type OptionIsDisabled = {
  option: FilterDataOption;
  filter: Filter;
  isLoading: boolean;
  useEvoFilters: boolean | undefined;
};

/** Evo keeps a selected option clickable even when it has no results, so a selection is never stuck. */
export const optionIsDisabled = ({ option, filter, isLoading, useEvoFilters }: OptionIsDisabled): boolean => {
  const staysRemovable = !!useEvoFilters && !!option.selected;
  return !!((option.disabled && !staysRemovable) || filter.disabled || isLoading);
};

export const filterOptions = (filter: Filter): FilterDataOption[] =>
  'options' in filter.data && filter.data.options ? filter.data.options : [];

const withOptions = (filter: Filter, options: FilterDataOption[]): Filter => ({
  ...filter,
  data: { ...filter.data, options },
});

/** The filter as it would be with exactly these values selected. Untouched if it has no options. */
export const withSelectedValues = (filter: Filter, values: string[]): Filter =>
  'options' in filter.data && filter.data.options
    ? withOptions(
        filter,
        filter.data.options.map((option) => ({ ...option, selected: values.includes(option.value) }))
      )
    : filter;

const onlyDefaultSelected = (filter: Filter, defaultValue: string): Filter =>
  withSelectedValues(filter, [defaultValue]);

export const toggleValues = (filter: Filter, values: string[]): Filter => {
  const { defaultValue, options } = multiSelectDataOf(filter);

  if (defaultValue !== undefined && values.includes(defaultValue)) return onlyDefaultSelected(filter, defaultValue);

  const selecting = !values.every((value) => options.find((option) => option.value === value)?.selected);

  return withOptions(
    filter,
    options.map((option) => {
      if (option.value === defaultValue) return { ...option, selected: false };
      return values.includes(option.value) ? { ...option, selected: selecting } : option;
    })
  );
};

export const withDefaultWhenEmpty = (filter: Filter): Filter => {
  const { defaultValue, options } = multiSelectDataOf(filter);
  if (defaultValue === undefined || options.some((option) => option.selected)) return filter;

  return onlyDefaultSelected(filter, defaultValue);
};

export const applyDefaultValueWhenNoSelected = (filter: Filter, handleFilterChanged: HandleFilterChanged) => {
  const repaired = withDefaultWhenEmpty(filter);
  if (repaired !== filter) handleFilterChanged([repaired]);
};
